import { SERVER_REGIONS } from './helpers.new';
import type { AccessLevel, CameraPermission } from '../types/permissions';
import type { Resolution } from '../types/cameras';
import { getOrgsWithRegions, getUid, updateOrgRegions } from './managers/store';
import { makePrivateRequestAsUser, makePrivateRequestWithData } from './managers/requestBuilder';
import type { PrivateRequestData } from './managers/requestBuilder';
import { getValidLogger } from './logger';
import { getOrganisationsLoggedInUserHasAccessTo } from './user';
import { logError } from '../utils/errors';
import { updateSmartMotionAccessCache } from '../utils/smartMotionAlertsAccess';

/** Recorded video quality as returned by the API. Adds 'SD' to {@link Resolution}. */
export type RecordingResolution = 'SD' | Resolution;

/**
 * One camera exactly as GET /cameras returns it.
 *
 * Mirrors `CameraWithRegion` in auth (types/cameras.ts) plus the `permission`
 * the handler attaches per camera, i.e. auth's `CameraResponse`. Keep the two
 * in step: every field here is a column or alias in `getDevicesFromDB`, so a
 * rename on the auth side is a silent `undefined` on this one.
 */
export interface CameraFromApi {
    uid: number;
    id: number;
    uninstalled: 0 | 1;
    logger: string | null;
    recordedStreamName: string | null;
    wowza: string | null;
    cloudRecordingEnabled: 0 | 1 | null;
    analyticsEnabled: 0 | 1;
    analyticsScheme: string | null;
    cloudAdapterId: string | null;
    macAddress: string | null;
    cameraId: string | null;
    model: string | null;
    timeZoneName: string | null;
    recordingResolution: RecordingResolution | null;
    romEnabled: 0 | 1;
    ptzEnabled: 0 | 1;
    talkbackEnabled: 0 | 1;
    audioEnabled: 0 | 1;
    mainstreamLive: 0 | 1 | null;
    cloudAdapterVersion: string;
    videoCodec: 'h264' | 'h265' | null;
    lastLogger: string | null;
    localLiveId: string | null;
    localLiveHosts: string[] | null;
    name: string;
    tags: string[] | null;
    region: SERVER_REGIONS;
    permission: CameraPermission | null;
    /**
     * Not returned by GET /cameras - the SD server comes from the camera's
     * status object, which is merged over this one downstream. Declared only so
     * the mapped object keeps the shape it has always had.
     */
    sdWowza?: string | null;
}

/** One camera in the shape the rest of the web expects. */
export interface Camera {
    logger: string | null;
    uidd: string;
    owner: number;
    deviceId: number;
    model: string | null;
    cloudRecordingEnabled: boolean;
    romEnabled: boolean;
    ptzEnabled: boolean;
    talkbackEnabled: boolean;
    audioEnabled: boolean;
    recordingResolution: RecordingResolution | null;
    wowza: string | null;
    sdWowza: string | null | undefined;
    name: string;
    analyticsEnabled: boolean;
    analyticsScheme: string | null;
    /** Own cameras have no `permission`, so they fall back to admin. */
    access: AccessLevel;
    tags: string[];
    cloudAdapterId: string | null;
    cloudAdapterVersion: string;
    localLiveHost: string[] | undefined;
    localLiveId: string | null;
    macAddress: string | null;
    camId: string | null;
    timezoneName: string | null;
    recordedStreamName: string | null;
    mainstreamLive: boolean;
    videoCodec: 'h264' | 'h265';
    lastLogger: number | null;
    region: SERVER_REGIONS;
    uninstalled: boolean;
}

/** Cameras keyed by uidd. */
export type CamerasByUidd = Record<string, Camera>;

/** Cameras keyed by device id. */
export type CamerasByDeviceId = Record<number, Camera>;

interface FetchCamerasOptions {
    /** Fetch one camera. Cannot be combined with `owner`. */
    uidd?: string;
    /** Fetch every camera owned by this account. Cannot be combined with `uidd`. */
    owner?: number;
}

/**
 * Takes one camera as GET /cameras returns it, returns the object the app uses.
 */
export const getCameraObject = ({
    logger,
    uid,
    id: deviceId,
    wowza,
    sdWowza,
    name,
    cloudRecordingEnabled,
    analyticsEnabled,
    analyticsScheme,
    audioEnabled,
    permission,
    romEnabled,
    ptzEnabled,
    talkbackEnabled,
    mainstreamLive,
    tags,
    cloudAdapterId,
    cloudAdapterVersion,
    localLiveHosts,
    localLiveId,
    macAddress,
    cameraId,
    recordingResolution,
    model,
    timeZoneName,
    recordedStreamName,
    videoCodec,
    lastLogger,
    region,
    uninstalled,
}: CameraFromApi): Camera => ({
    logger,
    uidd: `${uid}.${deviceId}`,
    owner: uid,
    deviceId,
    model,
    cloudRecordingEnabled: !!cloudRecordingEnabled,
    romEnabled: !!romEnabled,
    ptzEnabled: !!ptzEnabled,
    talkbackEnabled: !!talkbackEnabled,
    audioEnabled: !!audioEnabled,
    recordingResolution,
    wowza,
    sdWowza,
    name,
    analyticsEnabled: !!analyticsEnabled,
    analyticsScheme,
    // If no permission then this user is owner
    access: permission ?? 'a',
    tags: tags ?? [],
    cloudAdapterId,
    cloudAdapterVersion,
    // Local live: convert IP addresses to hosts
    localLiveHost: localLiveHosts?.map(host => `${host.replace(/[.:]/g, '-')}.${localLiveId}.videoloft.live`),
    localLiveId,
    // install info
    macAddress,
    camId: cameraId,
    timezoneName: timeZoneName,
    recordedStreamName,
    mainstreamLive: !!mainstreamLive,
    videoCodec: videoCodec ?? 'h264',
    lastLogger: lastLogger ? new Date(lastLogger).getTime() : null,
    region,
    uninstalled: !!uninstalled,
});

/**
 * Fetch cameras data for all cameras, a specific camera, or a specific account's
 * cameras. Can only specify one of `uidd` or `owner`.
 *
 * Cameras the user has no access to, and cameras that have been deleted, are not
 * included in the response.
 */
export const fetchCameras = async (
    { makePrivateRequest }: PrivateRequestData,
    { uidd, owner }: FetchCamerasOptions = {}
): Promise<CamerasByUidd> => {
    // The one place the GET /cameras contract is asserted: everything below is
    // typed off CameraFromApi, which mirrors auth's CameraResponse.
    const cameras = (
        await makePrivateRequest(
            'auth',
            'GET',
            '/cameras',
            {
                params: {
                    uidd,
                    owner
                }
            }
        )
    ).data as CameraFromApi[];

    /*
        The old /viewerInfo API used to return a valid logger for every camera, even offline ones, so
        the rest of the web is set up assuming every camera object will include a valid logger domain
        (used for e.g. fetching thumbs, events, etc.). When replacing /viewerInfo with the much faster
        new /cameras API we needed an alternative way to handle this as we didn't want this logic slowing
        down the new API. The frontend now copies logger domains where provided for online (or recently
        offline) cameras into the objects of cameras with no assigned logger. In the rare case none of
        the user's cameras have a valid logger, we have to fetch a domain from GET /logger.
    */

    // Whilst iterating through cameras, when we find a valid logger put it here for others
    let knownLogger: string | null = null;

    // Until we find a logger, store cameras missing a logger and hence need to be later updated here
    const camerasWithoutLogger: string[] = [];

    const cameraOwners = new Set<number>();

    // Format cameras array into an object mapping uidd to info
    const camerasObject = cameras.reduce<CamerasByUidd>((acc, cameraUnformatted) => {
        cameraOwners.add(cameraUnformatted.uid);

        const camera = getCameraObject(cameraUnformatted);

        if (!camera.logger) {
            // We need to find a logger to store against this camera
            if (knownLogger) {
                // We have already found a valid one from a previous camera's data
                camera.logger = knownLogger;
            } else {
                // We haven't yet found a valid logger
                camerasWithoutLogger.push(camera.uidd);
            }
        } else if (!knownLogger) {
            // This camera has a logger so store it for others
            knownLogger = camera.logger;
        }

        return {
            ...acc,
            [camera.uidd]: camera
        };
    }, {});

    const organisationIds = Object.keys(getOrgsWithRegions());

    // fetch organisations again as the user might have received permissions to new organisations since they were fetched last
    if (Array.from(cameraOwners).some(owner => !organisationIds.includes(String(owner)))) {
        const newOrgs = await getOrganisationsLoggedInUserHasAccessTo();
        updateOrgRegions(newOrgs);
    }

    if (camerasWithoutLogger.length > 0) {

        // We have some camera objects without a logger
        // Go back and populate them now

        if (!knownLogger) {
            // We did not have any cameras with a valid logger so need to fetch one
            knownLogger = await getValidLogger();
        }

        camerasWithoutLogger.forEach(uidd => {
            camerasObject[uidd].logger = knownLogger;
        });
    }

    return camerasObject;
};

const getCamerasByOwnerRequest = async (
    requestData: PrivateRequestData,
    ownerUid: number
): Promise<CamerasByDeviceId> => {

    const cameras = await fetchCameras(requestData, { owner: ownerUid });

    return Object.values(cameras).reduce<CamerasByDeviceId>((acc, camera) => {
        acc[camera.deviceId] = camera;
        return acc;
    }, {});
};

/**
 * Fetch all cameras owned by a user, keyed by device id.
 */
export const getCamerasByOwner: (ownerUid: number) => Promise<CamerasByDeviceId> =
    makePrivateRequestAsUser(getCamerasByOwnerRequest);

/**
 * As {@link getCamerasByOwner}, but for a caller that already has request data.
 *
 * NOTE: this is curried - it takes the request data and returns the function
 * that takes the owner uid. See the call site in api/user.js, which passes both
 * arguments at once and so receives the inner function rather than the cameras.
 */
export const getCamerasByOwnerUsingRequestData: (
    requestData: PrivateRequestData
) => (ownerUid: number) => Promise<CamerasByDeviceId> =
    requestData => makePrivateRequestWithData(requestData, getCamerasByOwnerRequest);

/**
 * Fetch info on a specific camera, keyed by uidd.
 *
 * If the user has no access to the camera, it does not exist, or it has been
 * deleted, an empty object is returned.
 */
export const getCamera: (uidd: string) => Promise<CamerasByUidd> =
    uidd => makePrivateRequestAsUser(fetchCameras)({ uidd });

/**
 * Every camera the logged in user has access to, keyed by uidd.
 */
export const getAllCameras: (options?: FetchCamerasOptions) => Promise<CamerasByUidd> =
    makePrivateRequestAsUser(async (requestData: PrivateRequestData, filter?: FetchCamerasOptions) => {
        const cameras = await fetchCameras(requestData, filter);

        // Opportunistically keep the "does this user have access to a smart
        // motion enabled camera" cache fresh, since this is called very often to fetch
        // the full, unfiltered set of cameras the user has access to. This is what keeps the Alerts
        // nav link (see AppLayout) up to date without every page needing to know about it
        // a bit smelly for a solution but there is no reactive storage actually setup atm
        if (!filter?.uidd && !filter?.owner) {
            try {
                updateSmartMotionAccessCache(getUid(), cameras);
            } catch (error) {
                logError(error as Error, { identifier: 'getAllCameras - updateSmartMotionAccessCache' });
            }
        }

        return cameras;
    });
